import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapSysDeptMutationPayload,
  mapSysStaffAddDeptPayload,
  mapSysStaffListQueryParams,
  mapSysStaffMutationPayload,
  mapSysStaffMoveDeptPayload,
  mapSysStaffPasswordResetPayload,
  mapSysStaffStatusPayload,
  mapSysStaffSupervisorsPayload,
  type SysDeptStaffTreeItemRaw,
  type SysDeptTreeNodeRaw,
  type SysDeptTreePayloadRaw,
  type SysStaffItemRaw,
  type SysStaffListPayloadRaw,
  type SysStaffPasswordResetPayloadRaw,
  type SysStaffSupervisorPayloadRaw,
  type SysStaffSupervisorRowRaw,
} from "../api/adapters";
import type {
  SysDeptMutation,
  SysDeptTreeParams,
  SysDeptStaffTreeItem,
  SysDeptTreeNode,
  SysStaffItem,
  SysStaffAddDeptMutation,
  SysStaffListParams,
  SysStaffListPayload,
  SysStaffMutation,
  SysStaffMoveDeptMutation,
  SysStaffPasswordResetMutation,
  SysStaffPasswordResetPayload,
  SysStaffStatusMutation,
  SysStaffSupervisorItem,
  SysStaffSupervisorMutationItem,
  SysStaffSupervisorPayload,
} from "../types";

/** 查询部门树。 */
export async function fetchSysDeptTree(params: SysDeptTreeParams): Promise<SysDeptTreeNode[]> {
  const response = await httpClient.get<ApiEnvelope<SysDeptTreePayloadRaw>>("/sys/dept-users/depts/tree", {
    params: { include_disabled: params.includeDisabled },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("部门树接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapSysDeptTreeNode);
}

/** 创建部门。 */
export async function createSysDept(payload: SysDeptMutation): Promise<SysDeptTreeNode> {
  const response = await httpClient.post<ApiEnvelope<SysDeptTreeNodeRaw>>(
    "/sys/dept-users/depts",
    mapSysDeptMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("创建部门接口返回了无效数据");
  }
  return mapSysDeptTreeNode(response.data.data);
}

/** 编辑部门。 */
export async function updateSysDept(deptId: number, payload: SysDeptMutation): Promise<SysDeptTreeNode> {
  const response = await httpClient.put<ApiEnvelope<SysDeptTreeNodeRaw>>(
    `/sys/dept-users/depts/${deptId}`,
    mapSysDeptMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("编辑部门接口返回了无效数据");
  }
  return mapSysDeptTreeNode(response.data.data);
}

/** 查询用户列表。 */
export async function fetchSysStaffList(params: SysStaffListParams): Promise<SysStaffListPayload> {
  const response = await httpClient.get<ApiEnvelope<SysStaffListPayloadRaw>>("/sys/dept-users/staff", {
    params: mapSysStaffListQueryParams(params),
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("用户列表接口返回了无效数据");
  }
  return mapSysStaffListPayload(response.data.data);
}

/** 创建用户。 */
export async function createSysStaff(payload: SysStaffMutation): Promise<SysStaffItem> {
  const response = await httpClient.post<ApiEnvelope<SysStaffItemRaw>>(
    "/sys/dept-users/staff",
    mapSysStaffMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("创建用户接口返回了无效数据");
  }
  return mapSysStaffItem(response.data.data);
}

/** 编辑用户。 */
export async function updateSysStaff(userId: number, payload: SysStaffMutation): Promise<SysStaffItem> {
  const response = await httpClient.put<ApiEnvelope<SysStaffItemRaw>>(
    `/sys/dept-users/staff/${userId}`,
    mapSysStaffMutationPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("编辑用户接口返回了无效数据");
  }
  return mapSysStaffItem(response.data.data);
}

/** 切换用户状态。 */
export async function updateSysStaffStatus(
  userId: number,
  payload: SysStaffStatusMutation,
): Promise<SysStaffItem> {
  const response = await httpClient.put<ApiEnvelope<SysStaffItemRaw>>(
    `/sys/dept-users/staff/${userId}/status`,
    mapSysStaffStatusPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("用户状态接口返回了无效数据");
  }
  return mapSysStaffItem(response.data.data);
}

/** 移动员工到指定部门。 */
export async function moveSysStaffDept(
  userId: number,
  payload: SysStaffMoveDeptMutation,
): Promise<SysStaffItem> {
  const response = await httpClient.put<ApiEnvelope<SysStaffItemRaw>>(
    `/sys/dept-users/staff/${userId}/dept`,
    mapSysStaffMoveDeptPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("员工移动部门接口返回了无效数据");
  }
  return mapSysStaffItem(response.data.data);
}

/** 追加员工归属部门。 */
export async function addSysStaffToDept(
  userId: number,
  payload: SysStaffAddDeptMutation,
): Promise<SysStaffItem> {
  const response = await httpClient.post<ApiEnvelope<SysStaffItemRaw>>(
    `/sys/dept-users/staff/${userId}/depts`,
    mapSysStaffAddDeptPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("员工追加部门接口返回了无效数据");
  }
  return mapSysStaffItem(response.data.data);
}

/** 管理员重置用户密码。 */
export async function resetSysStaffPassword(
  userId: number,
  payload: SysStaffPasswordResetMutation,
): Promise<SysStaffPasswordResetPayload> {
  const response = await httpClient.put<ApiEnvelope<SysStaffPasswordResetPayloadRaw>>(
    `/auth/admin/users/${userId}/password`,
    mapSysStaffPasswordResetPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("重置密码接口返回了无效数据");
  }
  return {
    userId: response.data.data.user_id,
  };
}

/** 查询员工主管。 */
export async function fetchSysStaffSupervisors(userId: number): Promise<SysStaffSupervisorPayload> {
  const response = await httpClient.get<ApiEnvelope<SysStaffSupervisorPayloadRaw>>(
    `/sys/dept-users/staff/${userId}/supervisors`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("员工主管接口返回了无效数据");
  }
  return mapSysStaffSupervisorPayload(response.data.data);
}

/** 保存员工主管。 */
export async function updateSysStaffSupervisors(
  userId: number,
  payload: SysStaffSupervisorMutationItem[],
): Promise<SysStaffSupervisorPayload> {
  const response = await httpClient.put<ApiEnvelope<SysStaffSupervisorPayloadRaw>>(
    `/sys/dept-users/staff/${userId}/supervisors`,
    mapSysStaffSupervisorsPayload(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("保存员工主管接口返回了无效数据");
  }
  return mapSysStaffSupervisorPayload(response.data.data);
}

/** 映射部门树节点响应。 */
function mapSysDeptTreeNode(raw: SysDeptTreeNodeRaw): SysDeptTreeNode {
  return {
    id: raw.id,
    parentId: raw.parent_id,
    deptName: raw.dept_name,
    orderNum: raw.order_num,
    leaderId: raw.leader_id ?? undefined,
    leaderName: raw.leader_name ?? "",
    approverId: raw.approver_id ?? undefined,
    approverName: raw.approver_name ?? "",
    phone: raw.phone ?? "",
    email: raw.email ?? "",
    status: raw.status,
    statusLabel: raw.status_label,
    userCount: raw.user_count,
    staffItems: (raw.staff_items ?? []).map(mapSysDeptStaffTreeItem),
    children: (raw.children ?? []).map(mapSysDeptTreeNode),
  };
}

/** 映射部门树员工节点响应。 */
function mapSysDeptStaffTreeItem(raw: SysDeptStaffTreeItemRaw): SysDeptStaffTreeItem {
  return {
    userId: raw.user_id,
    username: raw.username,
    displayName: raw.display_name,
    mainDeptId: raw.main_dept_id ?? undefined,
    status: raw.status,
    statusLabel: raw.status_label,
  };
}

/** 映射用户分页响应。 */
function mapSysStaffListPayload(raw: SysStaffListPayloadRaw): SysStaffListPayload {
  return {
    items: (raw.items ?? []).map(mapSysStaffItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 映射用户明细响应。 */
function mapSysStaffItem(raw: SysStaffItemRaw): SysStaffItem {
  return {
    id: raw.id,
    username: raw.username,
    realname: raw.realname,
    nickname: raw.nickname,
    phone: raw.phone,
    email: raw.email,
    avatar: raw.avatar ?? "",
    sex: raw.sex ?? undefined,
    mainDeptId: raw.main_dept_id ?? undefined,
    mainDeptName: raw.main_dept_name,
    status: raw.status,
    statusLabel: raw.status_label,
    userType: raw.user_type ?? 1,
    supplierId: raw.supplier_id ?? undefined,
    wxOpenid: raw.wx_openid ?? "",
    wxUnionid: raw.wx_unionid ?? "",
    isSubscribe: raw.is_subscribe ?? false,
    isSuperuser: raw.is_superuser ?? false,
    isStaff: raw.is_staff,
    deptIds: raw.dept_ids ?? [],
    deptNames: raw.dept_names ?? [],
    supervisors: (raw.supervisors ?? []).map(mapSysStaffSupervisorItem),
    createTime: raw.create_time,
    updateTime: raw.update_time,
  };
}

/** 映射员工主管响应。 */
function mapSysStaffSupervisorPayload(raw: SysStaffSupervisorPayloadRaw): SysStaffSupervisorPayload {
  return {
    items: (raw.items ?? []).map(mapSysStaffSupervisorItem),
  };
}

/** 映射单条主管响应。 */
function mapSysStaffSupervisorItem(raw: SysStaffSupervisorRowRaw): SysStaffSupervisorItem {
  return {
    supervisorId: raw.supervisor_id,
    supervisorName: raw.supervisor_name,
    supervisorLevel: raw.supervisor_level,
    orderNum: raw.order_num,
  };
}
